import { remoteManifestUrl } from '@libs/focus-frontend-build/src/releaseManifest';
import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime';

import { reloadOnce, resolvedRelease } from './releaseResolution';
import reportRemoteLoadFailure, { isRemoteUnreachable } from './reportRemoteLoadFailure';

const LOCAL_BUILD = 'development';

const remoteNameOf = (id: string) => id.split('/')[0];

const releaseResolutionPlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'release-resolution',
  async beforeRequest(args) {
    const release = await resolvedRelease();

    if (!release) {
      if (environment.BUILD_IDENTIFIER === LOCAL_BUILD) {
        return args;
      }
      throw new Error(`No release pins host build ${environment.BUILD_IDENTIFIER}, so ${args.id} cannot be loaded.`);
    }

    const requested = remoteNameOf(args.id);

    if (!release.remotes[requested]) {
      throw new Error(`Release ${release.release} pins no version of ${requested}, so ${args.id} cannot be loaded.`);
    }

    args.options.remotes.forEach((remote) => {
      const version = release.remotes[remote.name];

      if (version && 'entry' in remote) {
        remote.entry = remoteManifestUrl(remote.name, version);
      }
    });

    return args;
  },
  beforePreloadRemote({ preloadOps }) {
    preloadOps.splice(0);
  },
  async errorLoadRemote({ id, remote, error, lifecycle }) {
    const release = await resolvedRelease();
    const name = remote?.name ?? remoteNameOf(id);
    const version = release?.remotes[name];

    if (release && version && isRemoteUnreachable(error)) {
      await reportRemoteLoadFailure({
        remote: name,
        version,
        entry: remoteManifestUrl(name, version),
        release: release.release,
        lifecycle,
        error,
      });
      reloadOnce();
    }

    return undefined;
  },
});

export default releaseResolutionPlugin;
